import { Fragment, type ReactElement } from 'react'
import { Layers, Magnet, MessageSquare, MousePointer2, MoveUpRight, Trash2, Type } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ChannelIcon,
  FibonacciIcon,
  HorizontalLineIcon,
  LongPositionIcon,
  RectangleIcon,
  ShortPositionIcon,
  TrendlineIcon,
  VerticalLineIcon,
} from './WorkbenchDrawIcons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip/Tooltip'
import type { ToolDef } from './types'

// Workbench drawing rail (TradingView-style left rail). Buttons map to
// klinecharts overlay names; the parent calls createOverlay. "Cursor" clears the
// active tool. Drawing icons are TV-style hand-drawn glyphs (hollow-circle handles).
//
// Buy/Sell start a TRADE IDEA: the chart then leads through entry → stop loss →
// take profit, one click each, refusing any level on the wrong side of the entry.
// Buy = long, Sell = short. The Layers button lists every idea on the chart.

export const CURSOR_TOOL: ToolDef = { id: 'cursor', overlay: null, label: 'Cursor', Icon: MousePointer2 }

export const RAIL_TOOLS: ToolDef[] = [
  // Trade-idea tools first (right after the cursor) — the primary act here.
  // The list button joins them with no separator: it belongs to this group.
  { id: 'buy', idea: 'buy', label: 'Buy (start a trade idea)', Icon: LongPositionIcon, iconClass: 'text-success', text: 'BUY', sep: true },
  { id: 'sell', idea: 'sell', label: 'Sell (start a trade idea)', Icon: ShortPositionIcon, iconClass: 'text-danger', text: 'SELL' },
  { id: 'trade-ideas', panel: 'tradeIdeas', label: 'Trade ideas', Icon: Layers },
  // Notes group, right below the trade markers: free text + a callout whose tail pins
  // to a candle's O/H/L/C. Both type inline and travel to the AI.
  { id: 'text', note: 'text', click: true, label: 'Text', Icon: Type, sep: true },
  { id: 'callout', note: 'callout', click: true, label: 'Callout (note pinned to a candle)', Icon: MessageSquare },
  // Then the generic drawing tools, divided off by a separator. Arrow sits right below the trend line.
  { id: 'trend', overlay: 'segment', label: 'Trend line', Icon: TrendlineIcon, sep: true },
  { id: 'arrow', overlay: 'arrow', label: 'Arrow', Icon: MoveUpRight },
  { id: 'horizontal', overlay: 'horizontalStraightLine', label: 'Horizontal line', Icon: HorizontalLineIcon },
  { id: 'vertical', overlay: 'verticalStraightLine', label: 'Vertical line', Icon: VerticalLineIcon },
  { id: 'channel', overlay: 'parallelStraightLine', label: 'Parallel channel', Icon: ChannelIcon },
  { id: 'fib', overlay: 'fibRetracement', label: 'Fibonacci', Icon: FibonacciIcon },
  { id: 'rectangle', overlay: 'rectangle', label: 'Rectangle', Icon: RectangleIcon },
]

// A themed tooltip wrapper (replaces the slow, native `title` tooltips). Appears to
// the right of the rail, fast. Wraps any single trigger element via asChild.
function Hint({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  )
}

const toolBtn = (active: boolean) =>
  cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer',
    active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
  )

export interface WorkbenchDrawingRailProps {
  activeTool: string
  onSelectTool: (tool: ToolDef) => void
  onOpenDeleteMenu: (pos: { x: number; y: number }) => void
  magnet: boolean
  onToggleMagnet: () => void
  /** The Trade ideas panel is a docked view, not an armed tool — it reads its
   *  own pressed state rather than sharing `activeTool`. */
  tradeIdeasOpen?: boolean
  onToggleTradeIdeas?: () => void
  /**
   * Read-only mode (Walkthrough). Every control is disabled rather than hidden, so the rail
   * keeps its width and the chart beside it does not resize on a mode flip.
   */
  disabled?: boolean
}

export function WorkbenchDrawingRail({
  activeTool,
  onSelectTool,
  onOpenDeleteMenu,
  magnet,
  onToggleMagnet,
  tradeIdeasOpen = false,
  onToggleTradeIdeas,
  disabled = false,
}: WorkbenchDrawingRailProps) {
  const hint = (label: string) => (disabled ? 'Not available in Walkthrough' : label)
  return (
    <TooltipProvider delayDuration={150}>
      <div
        data-testid="drawing-rail"
        aria-disabled={disabled || undefined}
        className={cn(
          'shrink-0 w-11 flex flex-col items-center gap-0.5 py-2 border-r border-border bg-background',
          disabled && 'opacity-50',
        )}
      >
        {/* Cursor */}
        <Hint label={hint(CURSOR_TOOL.label)}>
          <button
            type="button"
            aria-label={CURSOR_TOOL.label}
            aria-pressed={activeTool === CURSOR_TOOL.id}
            data-tool={CURSOR_TOOL.id}
            disabled={disabled}
            onClick={() => onSelectTool(CURSOR_TOOL)}
            className={cn(toolBtn(activeTool === CURSOR_TOOL.id), disabled && 'cursor-not-allowed')}
          >
            <CURSOR_TOOL.Icon size={16} strokeWidth={1.75} />
          </button>
        </Hint>

        {/* Drawing tools (incl. Buy/Sell, which start a trade idea) */}
        {RAIL_TOOLS.map((t) => {
          const isPanel = !!t.panel
          const isActive = isPanel ? tradeIdeasOpen : activeTool === t.id
          return (
            <Fragment key={t.id}>
              {t.sep && <div className="my-1 w-5 border-t border-border" />}
              <Hint label={hint(t.label)}>
                <button
                  type="button"
                  aria-label={t.label}
                  aria-pressed={isActive}
                  data-tool={t.id}
                  disabled={disabled}
                  onClick={() => (isPanel ? onToggleTradeIdeas?.() : onSelectTool(t))}
                  className={cn(toolBtn(isActive), disabled && 'cursor-not-allowed')}
                >
                  {t.text ? (
                    <span className={cn('text-[9px] font-semibold leading-none tracking-tight', t.iconClass)}>{t.text}</span>
                  ) : (
                    <t.Icon size={16} strokeWidth={1.75} className={cn(t.rotate && 'rotate-90', t.iconClass)} />
                  )}
                </button>
              </Hint>
            </Fragment>
          )
        })}

        <div className="mt-1 pt-1 border-t border-border w-7 flex flex-col items-center gap-0.5">
          {/* Magnet: snap drawing points to the nearest OHLC while drawing (toggle) */}
          <Hint label={hint('Magnet (snap to OHLC while drawing)')}>
            <button
              type="button"
              aria-label="Magnet"
              aria-pressed={magnet}
              disabled={disabled}
              onClick={onToggleMagnet}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                magnet ? 'bg-muted text-primary' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              <Magnet size={16} strokeWidth={1.75} />
            </button>
          </Hint>
          <Hint label={hint('Remove drawings / indicators')}>
            <button
              type="button"
              aria-label="Remove"
              disabled={disabled}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect()
                onOpenDeleteMenu({ x: r.right + 6, y: r.top })
              }}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <Trash2 size={16} strokeWidth={1.75} />
            </button>
          </Hint>
        </div>
      </div>
    </TooltipProvider>
  )
}
